/** XeAgents.ai**/
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  BarChart3,
  Bot,
  BookOpen,
  CalendarCheck,
  ClipboardList,
  Code2,
  Coins,
  Contact,
  FileText,
  Gauge,
  Globe,
  Headphones,
  Heart,
  Languages,
  LineChart,
  Mic,
  Phone,
  PhoneIncoming,
  Plug,
  Radar,
  Sparkles,
  Target,
  Ticket,
  Users,
  Webhook,
  Workflow,
  type LucideIcon,
} from "lucide-react";

interface Capability {
  icon: LucideIcon;
  /** Key under platform.items.* in the landing namespace. */
  id: string;
  /** Shown as a small pill. Use only where the feature is not generally available. */
  tag?: string;
  /** Leads its group on a wider, accented card. Reserve it for the headline capability. */
  featured?: boolean;
}

interface CapabilityGroup {
  id: string;
  icon: LucideIcon;
  items: Capability[];
}

/**
 * The product's own feature list, grouped the way the dashboard sidebar groups
 * it, so what the landing page promises and what a new account actually opens
 * onto are the same thing.
 *
 * Everything here maps to a real page under /app. Anything not generally
 * available carries a tag rather than being quietly listed alongside the rest.
 */
const GROUPS: CapabilityGroup[] = [
  {
    id: "build",
    icon: Bot,
    items: [
      {
        icon: Radar,
        id: "aiSdr",
        featured: true,
      },
      {
        icon: Bot,
        id: "voiceAgents",
      },
      {
        icon: BookOpen,
        id: "knowledgeBase",
      },
      {
        icon: Mic,
        id: "voiceLibrary",
      },
      {
        icon: FileText,
        id: "promptTemplates",
      },
    ],
  },
  {
    id: "engage",
    icon: Phone,
    items: [
      {
        icon: Target,
        id: "campaigns",
      },
      {
        icon: PhoneIncoming,
        id: "inboundRouting",
      },
      {
        icon: Contact,
        id: "contacts",
      },
      {
        icon: Headphones,
        id: "recordings",
      },
      {
        icon: CalendarCheck,
        id: "followUpQueue",
      },
      {
        icon: Users,
        id: "crm",
      },
    ],
  },
  {
    id: "automate",
    icon: Workflow,
    items: [
      {
        icon: Workflow,
        id: "flowBuilder",
      },
      {
        icon: Sparkles,
        id: "aiFlow",
      },
      {
        icon: CalendarCheck,
        id: "booking",
      },
      {
        icon: ClipboardList,
        id: "forms",
      },
      {
        icon: Webhook,
        id: "webhooks",
      },
      {
        icon: Gauge,
        id: "executionLogs",
      },
    ],
  },
  {
    id: "measure",
    icon: LineChart,
    items: [
      {
        icon: BarChart3,
        id: "analytics",
      },
      {
        icon: Heart,
        id: "sentiment",
      },
      {
        icon: Target,
        id: "leadScoring",
      },
      {
        icon: LineChart,
        id: "campaignPerformance",
      },
      {
        icon: FileText,
        id: "reportExport",
      },
      {
        icon: Languages,
        id: "languages",
      },
    ],
  },
  {
    id: "operate",
    icon: Plug,
    items: [
      {
        icon: Users,
        id: "team",
      },
      {
        icon: Phone,
        id: "phoneNumbers",
      },
      {
        icon: Plug,
        id: "integrations",
      },
      {
        icon: Code2,
        id: "api",
      },
      {
        icon: Coins,
        id: "billing",
      },
      {
        icon: Ticket,
        id: "helpDesk",
      },
    ],
  },
];

const TOTAL = GROUPS.reduce((sum, group) => sum + group.items.length, 0);

export function PlatformCapabilities() {
  const { t } = useTranslation("landing");
  const [active, setActive] = useState(GROUPS[0].id);
  const shouldReduceMotion = useReducedMotion();

  const group = GROUPS.find((candidate) => candidate.id === active) ?? GROUPS[0];

  return (
    <div
      className="relative overflow-hidden bg-slate-950 py-16 sm:py-20 md:py-24"
      data-testid="platform-capabilities"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(139,92,246,0.22),transparent)]" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-fuchsia-600/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-violet-400/25 bg-violet-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-violet-200">
            <Globe className="h-3.5 w-3.5" />
            {t("platform.eyebrow")}
          </span>
          <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl">
            {t("platform.titleCount", { count: TOTAL })}
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              {" "}
              {t("platform.titleHighlight")}
            </span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-300">
            {t("platform.body")}
          </p>
        </motion.div>

        {/* ───────── Group switcher ───────── */}
        <div
          className="mt-10 flex flex-wrap justify-center gap-2 sm:gap-3"
          role="tablist"
          aria-label={t("platform.tabsLabel")}
        >
          {GROUPS.map((candidate) => {
            const Icon = candidate.icon;
            const isActive = candidate.id === active;
            return (
              <button
                key={candidate.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(candidate.id)}
                className={`group inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all sm:px-5 ${
                  isActive
                    ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-900/40"
                    : "border border-white/10 bg-white/5 text-slate-300 hover:border-violet-400/30 hover:text-white"
                }`}
                data-testid={`tab-capabilities-${candidate.id}`}
              >
                <Icon className="h-4 w-4" />
                {t(`platform.groups.${candidate.id}.label`)}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                    isActive ? "bg-white/20 text-white" : "bg-white/10 text-slate-400"
                  }`}
                >
                  {candidate.items.length}
                </span>
              </button>
            );
          })}
        </div>

        <p className="mt-5 text-center text-sm text-slate-400">{t(`platform.groups.${group.id}.blurb`)}</p>

        {/* ───────── Cards ───────── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={group.id}
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: -12 }}
            transition={{ duration: 0.28 }}
            className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid={`capability-group-${group.id}`}
          >
            {group.items.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.id}
                  initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.04 }}
                  className={`group relative overflow-hidden rounded-2xl border p-5 transition-all hover:-translate-y-0.5 hover:bg-white/[0.07] ${
                    item.featured
                      ? "border-violet-400/40 bg-gradient-to-br from-violet-500/20 via-fuchsia-500/10 to-transparent shadow-lg shadow-violet-500/10 ring-1 ring-violet-400/20 hover:border-violet-400/60 sm:col-span-2"
                      : "border-white/10 bg-white/[0.04] hover:border-violet-400/30"
                  }`}
                  data-testid={`capability-${item.id}`}
                >
                  <div className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl transition-colors group-hover:bg-violet-500/20 ${
                    item.featured ? "bg-violet-500/25" : "bg-violet-500/0"
                  }`} />
                  <div className="relative flex items-start justify-between gap-3">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/25 to-fuchsia-500/20 ring-1 ring-white/10">
                      <Icon className="h-5 w-5 text-violet-300" />
                    </span>
                    {item.featured && !item.tag && (
                      <span className="flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-400/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-200">
                        <Sparkles className="h-3 w-3" />
                        {t("platform.builtIn")}
                      </span>
                    )}
                    {item.tag && (
                      <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
                        {item.tag}
                      </span>
                    )}
                  </div>
                  <h3 className={`relative mt-4 font-semibold text-white ${item.featured ? "text-lg" : "text-base"}`}>
                    {t(`platform.items.${item.id}.name`)}
                  </h3>
                  <p className={`relative mt-1.5 text-sm leading-relaxed ${item.featured ? "text-slate-300" : "text-slate-400"}`}>
                    {t(`platform.items.${item.id}.detail`)}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default PlatformCapabilities;
